import { createModalEventManager } from "../../shared/util/modalEvents/createModalEventManager";
import type { UndoableSectionType } from "../context/UndoDelete/UndoDeleteContext";

/**
 * Payload for the `rnSectionItemDeleted` event. Emitted by a section's edit modal
 * after a successful delete so the owning profile section can show the same undo
 * banner its inline card-delete path shows. The modal lives in a separate React
 * root, so it signals the section via this event instead of calling
 * `setUndoState` directly.
 *
 * `sectionType` tells each section whether the delete belongs to it; `item` is
 * the minimal snapshot that section needs to rebuild undo state and restore the
 * item. Each section defines its own snapshot shape next to its undo logic (e.g.
 * `DeletedWorkExperience` for `'workExperience'`).
 */
export interface DeletedSectionItemPayload<T = unknown> {
  sectionType: UndoableSectionType;
  item: T;
}

type ProfileFormEvents = {
  rnFormCompleted: void;
  rnFormDeleted: void;

  /**
   * Emitted by a form that exits without saving after its save mutation failed.
   * The resume editor page listens for this to surface the generic save-error
   * banner, since the form itself has already been dismissed.
   */
  rnFormError: void;

  /**
   * Emitted by a section's edit modal after a successful delete so that section
   * can show the same undo banner the inline card-delete path shows.
   */
  rnSectionItemDeleted: DeletedSectionItemPayload;

  /**
   * Emitted by SkillsSpokeForm when the queued-delete flush fails on navigation
   * away. The resume editor page listens for this to surface an error banner.
   * `retry` re-attempts the flush and re-emits on subsequent failure.
   */
  rnSkillsFlushError: { retry: () => Promise<boolean> };
};

export const profileFormEvents = createModalEventManager<ProfileFormEvents>("ProfileForm");
